"use client";

import { useRouter } from "next/navigation";
import { useMemo } from "react";
import { Check } from "lucide-react";

const SortDropdown = ({
  options,
  selectedOption,
  onOptionChange,
  searchParams: searchParamsProp,
  isMobile = false,
}: {
  options: { id: string; label: string; value: string }[];
  selectedOption: string;
  onOptionChange: (option: string) => void;
  searchParams?: { [key: string]: string | string[] | undefined };
  isMobile?: boolean;
}) => {
  const router = useRouter();

  const searchParams = useMemo(() => {
    const params = new URLSearchParams();
    if (searchParamsProp) {
      Object.entries(searchParamsProp).forEach(([key, value]) => {
        if (value !== undefined) {
          const stringValue = Array.isArray(value) ? value[0] : value;
          if (stringValue !== undefined) {
            params.set(key, stringValue);
          }
        }
      });
    }
    return params;
  }, [searchParamsProp]);

  const handleOptionChange = (option: string) => {
    onOptionChange?.(option);
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set("sort_by", option);
    window.dispatchEvent(new Event("route-change-start"));
    router.push(`?${newParams.toString()}`, { scroll: false });
  };

  return (
    <ul
      className={
        isMobile
          ? "flex flex-col w-full font-outfit divide-y divide-gray-100"
          : "flex flex-col w-60 bg-white rounded-xl py-1 shadow-xl border border-gray-100/80 overflow-hidden font-outfit"
      }
    >
      {options.map((option, index) => {
        const isSelected = selectedOption === option.id;
        return (
          <li key={index} className="w-full">
            <button
              onClick={() => handleOptionChange(option.id)}
              className={
                isMobile
                  ? `flex items-center justify-between w-full py-4 text-left transition-all duration-200 ${
                      isSelected
                        ? "text-[#037A6A] font-bold"
                        : "text-[#434343] font-medium"
                    }`
                  : `flex items-center justify-between w-full px-5 py-4 text-left transition-all duration-200 ${
                      isSelected
                        ? "bg-[#effefc] text-[#037A6A] font-bold"
                        : "text-[#434343] hover:bg-stone-50 font-medium"
                    }`
              }
            >
              <span className="text-[15px] tracking-tight leading-tight">
                {option.label}
              </span>
              {isSelected ? (
                <div className="w-[18px] h-[18px] rounded-full bg-[#037A6A] flex items-center justify-center flex-shrink-0 ml-3 shadow-sm shadow-[#037A6A]/20">
                  <Check className="w-3 h-3 text-white stroke-[3px]" />
                </div>
              ) : (
                <div className="w-[18px] h-[18px] rounded-full border-[1.5px] border-stone-300 flex-shrink-0 ml-3 transition-colors duration-200" />
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
};

export default SortDropdown;
